import { Div, Label, Touchable } from "@grassrootly/rn-core";
import { GamePreviewProps } from "./GamePreview.types";
import moment from "moment";
import { isFinished } from "./lib";
import { Image, StyleSheet } from "react-native";
import { images } from "../../layout/assets";
import { GameType } from "../../screens/new-game/NewGame.types";

export default function GamePreviewInfo({ game, hideNotes, simpleView, navigation }: GamePreviewProps) {
  const showSimpleView = isFinished(game) || !!simpleView;
  const hasHostScore = game.host_score != null;
  const hasVisitorScore = game.visitor_score != null;
  const formattedDate = moment(game.datetime).format("DD.MM.YYYY HH:mm");
  const formattedLongDate = moment(game.datetime).format("ddd DD.MM.YYYY HH:mm");

  const handleGroupPress = () => {
    if (navigation && game.group_id) {
      navigation.navigate("group", { id: game.group_id });
    }
  };

  const handleHostTeamPress = () => {
    // For ORGANIZATION_MATCH: use host_group_id
    if (navigation && game.game_type === GameType.ORGANIZATION_MATCH && game.host_group_id) {
      navigation.navigate("group", { id: game.host_group_id });
    }
    // For COMPETITIVE: if playing as host, use group_id
    else if (navigation && game.game_type === GameType.COMPETITIVE && game.playing_as_host && game.group_id) {
      navigation.navigate("group", { id: game.group_id });
    }
  };

  const handleVisitorTeamPress = () => {
    // For ORGANIZATION_MATCH: use visitor_group_id
    if (navigation && game.game_type === GameType.ORGANIZATION_MATCH && game.visitor_group_id) {
      navigation.navigate("group", { id: game.visitor_group_id });
    }
    // For COMPETITIVE: if playing as visitor, use group_id
    else if (navigation && game.game_type === GameType.COMPETITIVE && !game.playing_as_host && game.group_id) {
      navigation.navigate("group", { id: game.group_id });
    }
  };

  return (
    <Div classNames={["bw-{1}", "borderPrimary", "w-{100%}", "br-{20}", "ph-{10}", "pt-{10}", "pb-{40}", "position-relative"]}>
      {showSimpleView ? (
        <Div classNames={["mt-{15}", "alignItemsCenter", "justifyContentCenter"]}>
          <Div classNames={["flexRow", "alignItemsCenter", "gap8", "mb-{8}"]}>
            <Label type="l3Regular">
              {formattedDate}
            </Label>
            {game.game_type === 'TRAINING' && (
              <Div classNames={["bgSecondaryLight", "rounded"]}>
                <Label classNames={["ph8", "pv2"]} type="l4Regular">
                  Training
                </Label>
              </Div>
            )}
          </Div>
          {game.game_type === 'TRAINING' && game.group ? (
            <Touchable
              onPress={handleGroupPress}
              disabled={!navigation}
              touchedOpacity={0.9}
            >
              <Div classNames={["flexRow", "alignItemsCenter", "gap8", "mb-{16}"]}>
                <Image
                  source={game.group.group_logo ? { uri: game.group.group_logo } : images.groupAvatar}
                  style={{ height: 20, width: 20, borderRadius: 4 }}
                  resizeMode="cover"
                />
                <Div classNames={["bgSecondaryMain", "rounded"]}>
                  <Label classNames={["ph16", "pv4"]} type="l2Bold">
                    {game.group.name}
                  </Label>
                </Div>
              </Div>
            </Touchable>
          ) : game.season_information ? (
            <Div classNames={["flexRow", "alignItemsCenter", "gap8", "mb-{16}"]}>
              {game.organization_logo && (
                <Image
                  source={{ uri: game.organization_logo }}
                  style={{ height: 20, width: 20, borderRadius: 4 }}
                  resizeMode="cover"
                />
              )}
              <Div classNames={["bgSecondaryMain", "rounded"]}>
                <Label classNames={["ph16", "pv4"]} type="l2Bold">
                  {game.season_information}
                </Label>
              </Div>
            </Div>
          ) : null}
        </Div>
      ) : null}

      <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "gap16"]}>
        {/* Host team */}
        <Touchable
          classNames={["flexColumn", "alignItemsCenter", "flex1", "justifyContentCenter"]}
          onPress={handleHostTeamPress}
          disabled={!(
            navigation && (
              (game.game_type === GameType.ORGANIZATION_MATCH && game.host_group_id) ||
              (game.game_type === GameType.COMPETITIVE && game.playing_as_host && game.group_id)
            )
          )}
          touchedOpacity={0.9}
        >
          <Image 
            source={game.host_team_logo ? { uri: game.host_team_logo } : images.whiteTeamLogo} 
            style={styles.logoLarge} 
            resizeMode="cover" 
          />
          <Label classNames={["mt-{8}", "textCenter"]} type="l2Bold">
            {game?.host_team_name || "Beli"}
          </Label>
        </Touchable>
        
        {/* Score separator */}
        <Div classNames={["alignItemsCenter", "justifyContentCenter"]}>
          {hasHostScore && hasVisitorScore ? (
            <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
              <Label type="l1Bold">{game.host_score}</Label>
              <Label type="l1Bold">-</Label>
              <Label type="l1Bold">{game.visitor_score}</Label>
            </Div>
          ) : (
            <Label type="l1Bold">-</Label>
          )}
        </Div>
        
        {/* Visitor team */}
        <Touchable
          classNames={["flexColumn", "alignItemsCenter", "flex1", "justifyContentCenter"]}
          onPress={handleVisitorTeamPress}
          disabled={!(
            navigation && (
              (game.game_type === GameType.ORGANIZATION_MATCH && game.visitor_group_id) ||
              (game.game_type === GameType.COMPETITIVE && !game.playing_as_host && game.group_id)
            )
          )}
          touchedOpacity={0.9}
        >
          <Image 
            source={game.visitor_team_logo ? { uri: game.visitor_team_logo } : images.blackTeamLogo} 
            style={styles.logoLarge} 
            resizeMode="cover" 
          />
          <Label classNames={["mt-{8}", "textCenter"]} type="l2Bold">
            {game?.visitor_team_name || "Markeri"}
          </Label>
        </Touchable>
      </Div>

      {!showSimpleView ? (
        <Div classNames={["alignItemsCenter", "justifyContentCenter", "mt-{16}"]}>
          <Label type="l2Regular">{formattedLongDate}</Label>
          {game.location ? (
            <Label type="l2Regular" classNames={["mt-{4}"]}>
              {game.location}
            </Label>
          ) : null}
          {game.game_type === 'TRAINING' && game.group ? (
            <Touchable
              onPress={handleGroupPress}
              disabled={!navigation}
              touchedOpacity={0.9}
            >
              <Div classNames={["flexRow", "alignItemsCenter", "gap8", "mt-{8}"]}>
                <Image
                  source={game.group.group_logo ? { uri: game.group.group_logo } : images.groupAvatar}
                  style={{ height: 20, width: 20, borderRadius: 4 }}
                  resizeMode="cover"
                />
                <Div classNames={["bgSecondaryMain", "rounded"]}>
                  <Label classNames={["ph16", "pv4"]} type="l2Bold">
                    {game.group.name}
                  </Label>
                </Div>
              </Div>
            </Touchable>
          ) : game.season_information ? (
            <Div classNames={["flexRow", "alignItemsCenter", "gap8", "mt-{8}"]}>
              {game.organization_logo && (
                <Image
                  source={{ uri: game.organization_logo }}
                  style={{ height: 20, width: 20, borderRadius: 4 }}
                  resizeMode="cover"
                />
              )}
              <Div classNames={["bgSecondaryMain", "rounded"]}>
                <Label classNames={["ph16", "pv4"]} type="l2Bold">
                  {game.season_information}
                </Label>
              </Div>
            </Div>
          ) : null}
          {game.notes && !hideNotes ? (
            <Label type="l3Bold" classNames={["mt-{24}"]}>
              {game.notes}
            </Label>
          ) : null}
        </Div>
      ) : null}
    </Div>
  );
}

const styles = StyleSheet.create({
  logo: {
    height: 20,
    width: 20,
    borderRadius: 40,
  },
  logoLarge: {
    height: 60,
    width: 60,
    borderRadius: 60,
  },
});
